import { LabelValue } from "../../../helpers/types/common";
import { reportsSearchReportsSiteCoreKeys } from "../siteCore/SiteCoreKeys";

export interface PendingBillsSummaryRow {
  key: string;
  clientName: string;
  type: string;
  status: string;
  participantName: string;
  localClientRelationshipOwnerName: string;
  fees: number;
  totalTaxInvoiced: number;
  totalExpenseInvoiced: number;
  totalFeesInvoiced: number;
  totalOutstandingAmount: number;
  billingContact: string;
  poNumbers: string[];
  productLineName: string;
  programTemplateName: string;
  alternateProgramTemplateName: string;
  hrContact: string;
  costCenter: string;
  businessUnit: string;
  deliveryOffice: string;
  nextView: string;
  actualStartDate: string;
}

export const pendingBillingListColumnsKey: Record<
  string,
  keyof PendingBillsSummaryRow
> = {
  key: "key",
  clientName: "clientName",
  type: "type",
  status: "status",
  participantName: "participantName",
  localClientRelationshipOwnerName: "localClientRelationshipOwnerName",
  fees: "fees",
  totalTaxInvoiced: "totalTaxInvoiced",
  totalExpenseInvoiced: "totalExpenseInvoiced",
  totalFeesInvoiced: "totalFeesInvoiced",
  totalOutstandingAmount: "totalOutstandingAmount",
  billingContact: "billingContact",
  poNumbers: "poNumbers",
  productLineName: "productLineName",
  programTemplateName: "programTemplateName",
  alternateProgramTemplateName: "alternateProgramTemplateName",
  hrContact: "hrContact",
  costCenter: "costCenter",
  businessUnit: "businessUnit",
  deliveryOffice: "deliveryOffice",
  nextView: "nextView",
  actualStartDate: "actualStartDate",
};

export interface NotificationStatusReportSummaryRow {
  programId: string;
  clientId: string;
  programKey: string;
  clientName: string;
  participantName: string;
  deliveryMarket: string;
  deliveryOffice: string;
  programTemplate: string;
  alternateDisplayName: string;
  notificationSupportDate: string;
  programStatus: string;
  programStatusDisplayValue?: string;
  outreachEligibleStatus: string;
  primaryManagedStatus: string;
  secondaryManagedStatus: string;
  stateRegionProvince?: string;
  primaryPhoneNumber: string;
  alternatePhoneNumber: string;
  primaryEmailId: string;
  registrationStatusNext: string;
  registrationStatusNextDisplayValue?: string;
}

export const NotificationStatusReportKey: Record<
  string,
  keyof NotificationStatusReportSummaryRow
> = {
  programId: "programId",
  clientId: "clientId",
  programKey: "programKey",
  clientName: "clientName",
  participantName: "participantName",
  deliveryMarket: "deliveryMarket",
  deliveryOffice: "deliveryOffice",
  programTemplate: "programTemplate",
  alternateDisplayName: "alternateDisplayName",
  notificationSupportDate: "notificationSupportDate",
  programStatus: "programStatus",
  programStatusDisplayValue: "programStatusDisplayValue",
  outreachEligibleStatus: "outreachEligibleStatus",
  primaryManagedStatus: "primaryManagedStatus",
  secondaryManagedStatus: "secondaryManagedStatus",
  stateRegionProvince: "stateRegionProvince",
  primaryPhoneNumber: "primaryPhoneNumber",
  alternatePhoneNumber: "alternatePhoneNumber",
  primaryEmailId: "primaryEmailId",
  registrationStatusNext: "registrationStatusNext",
  registrationStatusNextDisplayValue: "registrationStatusNextDisplayValue",
};

export interface OutreachReportSummaryRow {
  programId: string;
  clientId: string;
  programKey: string;
  clientName: string;
  participantName: string;
  marketForDeliveryOffice: string;
  deliveryOffice: string;
  programTemplate: string;
  outreachEligibleDate: string;
  nextContactDate: string;
  lastActivity: string;
  programStatus: string;
  programStatusDisplayValue?: string;
  mustStartByDate: string;
  stateRegionProvince?: string;
  contactWithin24Hrs: boolean;
  bestMatchPreference: string;
  alternateDisplayName: string;
  registrationCode: string;
  registrationStatusNext: string;
  registrationStatusNextDisplayValue?: string;
  notificationSupportDate: string;
  primaryEmailId: string;
  alternateEmailId: string;
  primaryPhoneNumber: string;
  alternatePhoneNumber: string;
  appendedOtherPhone: string;
  registrationEmailId: string;
  releaseDate: string;
  notificationDate: string;
  nisId: string;
}

export const OutreachReportKey: Record<string, keyof OutreachReportSummaryRow> =
  {
    programKey: "programKey",
    clientName: "clientName",
    participantName: "participantName",
    marketForDeliveryOffice: "marketForDeliveryOffice",
    deliveryOffice: "deliveryOffice",
    programTemplate: "programTemplate",
    outreachEligibleDate: "outreachEligibleDate",
    nextContactDate: "nextContactDate",
    lastActivity: "lastActivity",
    programStatus: "programStatus",
    programStatusDisplayValue: "programStatusDisplayValue",
    mustStartByDate: "mustStartByDate",
    stateRegionProvince: "stateRegionProvince",
    contactWithin24Hrs: "contactWithin24Hrs",
    bestMatchPreference: "bestMatchPreference",
    alternateDisplayName: "alternateDisplayName",
    registrationCode: "registrationCode",
    registrationStatusNext: "registrationStatusNext",
    registrationStatusNextDisplayValue: "registrationStatusNextDisplayValue",
    notificationSupportDate: "notificationSupportDate",
    primaryEmailId: "primaryEmailId",
    alternateEmailId: "alternateEmailId",
    appendedOtherPhone: "appendedOtherPhone",
    registrationEmailId: "registrationEmailId",
    releaseDate: "releaseDate",
    notificationDate: "notificationDate",
    nisId: "nisId",
    primaryPhoneNumber: "primaryPhoneNumber",
    alternatePhoneNumber: "alternatePhoneNumber",
  };

export interface OutreachReportDropdownTypes {
  programStatus: LabelValue[];
  contactWithin24Hrs: LabelValue[];
  registrationStatusNext: LabelValue[];
}

export interface NotificationStatusReportDropdownTypes {
  programStatus: LabelValue[];
  registrationStatusNext: LabelValue[];
  outreachEligibleStatus: LabelValue[];
  primaryManagedStatus: LabelValue[];
  secondaryManagedStatus: LabelValue[];
}

export interface PendingBillingSearchFormValues {
  categoryId: string;
  typeId: string;
}

export const selectPendingBillingFormKeys = {
  categoryId: "categoryId",
  typeId: "typeId",
} as const;

export interface PendingBillingSearchFormKeys {
  officeType: string;
  includeClosed?: string;
  clientIds?: string;
  outStandingAmount?: string;
  outStandingAmountWithFormat?: string;
}

export const searchPendingBillingFormKeys = {
  officeType: "officeType",
  includeClosed: "includeClosed",
  clientIds: "clientIds",
  outStandingAmount: "outStandingAmount",
  outStandingAmountWithFormat: "outStandingAmountWithFormat",
} as const;

export interface NotificationStatusReportFormKeys {
  marketOfDeliveryOffices: string[];
}

export const SearchNotificationStatusReportFormKeys = {
  marketOfDeliveryOffices: "marketOfDeliveryOffices",
} as const;

export interface OutreachReportFormKeys {
  marketOfDeliveryOffices: string[];
}

export const SearchOutreachReportFormKeys = {
  marketOfDeliveryOffices: "marketOfDeliveryOffices",
} as const;

export interface OutstandingAmountFormKeys {
  currency: string;
  minAmount: number;
  maxAmount: number;
  minAmountWithFormat: string;
  maxAmountWithFormat: string;
}
export const outstandingAmountFormKeys = {
  currency: "currency",
  minAmount: "minAmount",
  maxAmount: "maxAmount",
  minAmountWithFormat: "minAmountWithFormat",
  maxAmountWithFormat: "maxAmountWithFormat",
} as const;

export const outstandingAmountValidations = {
  minMaxAmountRequired: "minMaxRequired",
  minAmountGreaterThanZero: "minAmountGreaterThanZero",
  minAmountLessThanMaxAmount: "minLessThanMax",
  maxAmountGreaterThanMinAmount: "maxAmountGreaterThanMin",
};

export const outstandingAmountValidationMessagesMap = {
  [outstandingAmountValidations.minMaxAmountRequired]:
    reportsSearchReportsSiteCoreKeys.minMaxRequired,
  [outstandingAmountValidations.minAmountGreaterThanZero]:
    reportsSearchReportsSiteCoreKeys.minMustBeGreaterThanZero,
  [outstandingAmountValidations.minAmountLessThanMaxAmount]:
    reportsSearchReportsSiteCoreKeys.minMustBeLessThanMax,
  [outstandingAmountValidations.maxAmountGreaterThanMinAmount]:
    reportsSearchReportsSiteCoreKeys.maxMustBeGreaterThanMin,
} as const;

export interface PowerBiReportValues {
  id: string;
  name?: string;
  reportURL?: string;
  reportImageUrl?: string;
  reportSiteCoreId?: string;
  reportName?: string;
  reportImagePath?: string;
}

export interface CoachReportFormKeys {
  coaches: string[];
}

export const SearchCoachReportFormKeys = {
  coaches: "coaches",
} as const;

export interface CoachReportSummaryRow {
  assignedCoach: string;
  activeAssignments: string;
  openAssignments: string;
  inactiveAssignments: string;
}

export const CoachReportKey: Record<string, keyof CoachReportSummaryRow> = {
  assignedCoach: "assignedCoach",
  activeAssignments: "activeAssignments",
  openAssignments: "openAssignments",
  inactiveAssignments: "inactiveAssignments",
};

export const NCBPReportFormKeys = {
  dossierNumber: "dossierNumber",
  actualClientId: "actualClientId",
  nonClientBillingPartyId: "nonClientBillingPartyId",
  invoiceStartDate: "invoiceStartDate",
  invoiceEndDate: "invoiceEndDate",
} as const;

export interface NCBPReportFormKeyTypes {
  dossierNumber?: string;
  actualClientId?: string;
  nonClientBillingPartyId?: string;
  invoiceStartDate?: string | null;
  invoiceEndDate?: string | null;
}

export interface NCBPReportsSummaryRow {
  id: string;
  dossierNumber: string;
  keyNumber: string;
  type: string;
  isRecordTypeProgram: boolean;
  client: string;
  clientId: string;
  clientType: string;
  programProjectName: string;
  actualClient: string;
  actualClientId: string;
  invoiceNumber: string;
  invoiceDate: string;
  invoiceAmount: string;
  invoiceLineNumber: number;
  invoiceLineItemDetails: string;
  invoiceLineTotalBillingAmount: string;
}

export const NCBPReportsListColumnsKey: Record<
  string,
  keyof NCBPReportsSummaryRow
> = {
  dossierNumber: "dossierNumber",
  keyNumber: "keyNumber",
  type: "type",
  client: "client",
  clientType: "clientType",
  programProjectName: "programProjectName",
  actualClient: "actualClient",
  invoiceNumber: "invoiceNumber",
  invoiceDate: "invoiceDate",
  invoiceAmount: "invoiceAmount",
  invoiceLineNumber: "invoiceLineNumber",
  invoiceLineItemDetails: "invoiceLineItemDetails",
  invoiceLineTotalBillingAmount: "invoiceLineTotalBillingAmount",
};
